import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import SearchBlueprintType from '../../../types/SearchBlueprintType';
import InfiniteScrollTable from '../../InfiniteScrollTable';
import LoadingSpinner from '../../Spinner';

const SearchTable = ({ searchBlueprint, minHeight = 300, heightMultiplier }) => {
  const dispatch = useDispatch();
  const items = useSelector(searchBlueprint.selectData);
  const pageInfo = useSelector(searchBlueprint.selectPageInfo);
  const lastIndex = useSelector(searchBlueprint.selectLastIndex);
  const cols = useSelector(searchBlueprint.selectColumns);
  const isLoading = useSelector(searchBlueprint.selectIsLoading);
  const isLengthAwarePagination = useSelector(searchBlueprint.selectIsLengthAwarePagination);
  const hiddenColumns = useSelector(searchBlueprint.selectHiddenColumnIds);
  const sortBy = useSelector(searchBlueprint.selectSortBy);
  const quickFilters = useSelector(searchBlueprint.selectQuickFilterOptions);
  const advancedFilterOptions = useSelector(searchBlueprint.selectAdvancedFilterOptions);
  const advancedFilters = useSelector(searchBlueprint.selectAdvancedFilters);

  const columns = useMemo(() => cols, [cols]);
  const data = useMemo(() => items, [items]);
  const height = Math.max(minHeight, Math.min(data?.length * heightMultiplier, 500));

  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.el.scrollTo(0, 0);
    }
  }, [advancedFilters, quickFilters]);

  if (isLoading) {
    return (
      <Box bg="white" height={height}>
        <Box
          color="greyScale.200"
          position="relative"
          top="33%"
        >
          <LoadingSpinner isInline />
        </Box>
      </Box>
    );
  }

  if (!isLoading && !data.length) {
    return (
      <Box bg="white" height={height}>
        <Box
          color="greyScale.600"
          position="relative"
          top="33%"
        >
          No matching records.
        </Box>
      </Box>
    );
  }

  const pageData = isLengthAwarePagination
    ? { page: pageInfo.currentPage + 1 }
    : { after: lastIndex };

  return (
    <InfiniteScrollTable
      columns={columns}
      data={data}
      fetchMoreData={() => dispatch(searchBlueprint.getPage(pageData))}
      filters={[...quickFilters, ...advancedFilterOptions]}
      handleStateChange={changes => dispatch(searchBlueprint.handleStateChange(changes))}
      hasMore={pageInfo.hasMore}
      height={height}
      hiddenColumns={hiddenColumns}
      initialSortBy={sortBy}
      isLoading={isLoading}
      ref={scrollRef}
    />
  );
};

SearchTable.defaultProps = { heightMultiplier: 100 };

SearchTable.propTypes = {
  minHeight: PropTypes.number,
  searchBlueprint: SearchBlueprintType,
  heightMultiplier: PropTypes.number
};

export default SearchTable;
